import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api'

export interface ReportSprintRef {
  id: string
  name: string
  status: 'planning' | 'active' | 'completed' | string
}

export interface ReportBurndownDay {
  day: string
  /** Null for days that have not happened yet. */
  remaining: number | null
  ideal: number
}

export interface ReportBurndown {
  sprint_id: string
  name: string
  status: string
  start_date: string
  end_date: string
  /** Story points when the sprint has any, otherwise issue counts. */
  unit: 'points' | 'issues'
  total: number
  remaining: number
  days: ReportBurndownDay[]
}

export interface ReportVelocity {
  sprint_id: string
  name: string
  completed_at: string
  committed_points: number
  completed_points: number
  completed_count: number
}

export interface ReportFlowDay {
  day: string
  created: number
  resolved: number
}

export interface ReportFlow {
  days: number
  created_total: number
  resolved_total: number
  series: ReportFlowDay[]
}

export interface ReportBucket {
  key: string
  label: string
  count: number
}

export interface ReportBreakdown {
  open: number
  done: number
  by_status: ReportBucket[]
  by_assignee: ReportBucket[]
  by_priority: ReportBucket[]
  by_type: ReportBucket[]
}

export interface ProjectReports {
  project: string
  burndown: ReportBurndown | null
  sprints: ReportSprintRef[]
  velocity: ReportVelocity[]
  flow: ReportFlow
  breakdown: ReportBreakdown
}

export function fetchProjectReports(
  slug: string,
  projectKey: string,
  days: number,
  sprintId: string | null,
): Promise<ProjectReports> {
  const params = new URLSearchParams({ days: String(days) })
  if (sprintId) params.set('sprint', sprintId)
  return api.get<ProjectReports>(
    `/api/v2/orgs/${encodeURIComponent(slug)}/projects/${encodeURIComponent(projectKey)}/reports?${params}`,
  )
}

export function useProjectReports(
  slug: string | null,
  projectKey: string | null,
  days: number,
  sprintId: string | null,
) {
  return useQuery({
    queryKey: ['reports', slug ?? '', projectKey ?? '', days, sprintId ?? ''],
    queryFn: () => fetchProjectReports(slug as string, projectKey as string, days, sprintId),
    enabled: Boolean(slug && projectKey),
    staleTime: 30_000,
    // A 402 is an answer, not a blip: retrying it only delays the upgrade prompt.
    retry: false,
  })
}
